import { useCallback, useEffect, useRef, useState } from 'react';
import { captureReport, savePdf } from './reportExport';

// Chart.js draws a new line in over about a second; a picture taken sooner would show it half drawn.
const CHART_SETTLE_MS = 1100;
// How long a part of the page that is still loading (the map) is waited for.
const PENDING_LIMIT_MS = 6000;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Blocks elsewhere on the page that belong in the same PDF mark themselves with data-report-extra, and describe what
// they show in data-report-key ("loading" while their figures are on the way) and when it last changed in
// data-report-changed-at.
const findExtras = (element) => Array.from(document.querySelectorAll('[data-report-extra]')).filter((item) => !element.contains(item));

// The state behind a "Download PDF" button. `screenKey` names the venue, period and view the figures on screen belong to,
// and `loading` says they are on their way: the export looks at them before and after it works, so a picture is never
// made of one venue or period under the heading of another. Call `markChanged` whenever the figures or chart change.
export default function useReportExport({ screenKey, loading }) {
    const [exporting, setExporting] = useState(false);
    // null, "failed" (the picture could not be made) or "changed" (the venue, period or view changed while it was being made)
    const [problem, setProblem] = useState(null);
    const onScreen = useRef({ key: '', loading: false });
    const changedAt = useRef(0);
    useEffect(() => {
        onScreen.current = { key: screenKey, loading };
    });
    const markChanged = useCallback(() => { changedAt.current = Date.now(); }, []);

    const exportPdf = async ({ element, withExtras = false, venueName, reportName, rangeText, filename }) => {
        if (!element || exporting) return;
        const started = onScreen.current.key;
        const unchanged = () => onScreen.current.key === started && !onScreen.current.loading;
        const extraKeys = () => (withExtras ? findExtras(element).map((item) => item.dataset.reportKey).join('|') : '');
        setExporting(true);
        setProblem(null);
        try {
            // If the figures only just arrived, let the chart finish drawing so the picture shows all of it. A chart that changes
            // while this waits (another block of the ranking is chosen) starts the wait again, until none has changed lately.
            for (let round = 0; round < 20; round += 1) {
                const latest = Math.max(changedAt.current, ...(withExtras ? findExtras(element).map((item) => Number(item.dataset.reportChangedAt) || 0) : [0]));
                const settle = CHART_SETTLE_MS - (Date.now() - latest);
                if (settle <= 0) break;
                await wait(settle);
            }
            for (let waited = 0; element.querySelector('[data-report-pending]') && waited < PENDING_LIMIT_MS; waited += 150) await wait(150);
            if (element.querySelector('[data-report-pending]')) throw new Error('Part of the page was still loading.');
            if (!unchanged() || extraKeys().split('|').includes('loading')) { setProblem('changed'); return; }
            const keysBefore = extraKeys();
            const bytes = await captureReport({ element, extras: withExtras ? findExtras(element) : [], venueName, reportName, rangeText });
            if (!unchanged() || extraKeys() !== keysBefore) { setProblem('changed'); return; }
            savePdf(bytes, filename);
        } catch (error) {
            console.error('Could not create the PDF:', error);
            setProblem('failed');
        } finally {
            setExporting(false);
        }
    };

    return { exporting, problem, exportPdf, markChanged };
}

export const EXPORT_PROBLEMS = {
    changed: 'The figures changed while the PDF was being made, so it was not saved. Please try again.',
    failed: 'The PDF could not be created. Please try again.',
};
